// PulseTab — orchestrator for the QA Pulse tab.
// Ported from quality-live-portal/src/app/pulse/page.tsx and adapted for this
// app's session auth and inline-styled dashboard shell.

import { useCallback, useEffect, useMemo, useState } from 'react'
import AIQualityAnalysis from './AIQualityAnalysis.jsx'
import AiAutomationInsights from './AiAutomationInsights.jsx'
import AutomationCorner from './AutomationCorner.jsx'
import AutomationCoverageInsights, { CoverageWinsBulletin } from './AutomationCoverageInsights.jsx'
import BadgeChampions from './BadgeChampions.jsx'
import DefectMetrics from './DefectMetrics.jsx'
import ExportableSection from './ExportableSection.jsx'
import Leaderboard from './Leaderboard.jsx'
import QaDiagnosticsChart from './QaDiagnosticsChart.jsx'
import QaEngineerScorecards from './QaEngineerScorecards.jsx'
import QaFailedMetrics from './QaFailedMetrics.jsx'
import QaImpactSummary from './QaImpactSummary.jsx'
import QaMomentum from './QaMomentum.jsx'
import QaSprintHistory from './QaSprintHistory.jsx'
import QaStoryStatus from './QaStoryStatus.jsx'
import QaTeamComparison from './QaTeamComparison.jsx'
import QualityEngineerProgressChart from './QualityEngineerProgressChart.jsx'
import ReleaseComparison from './ReleaseComparison.jsx'
import SprintScoreGauge from './SprintScoreGauge.jsx'
import { PULSE_COLORS } from './pulseConfig.js'
import { aggregateSprints } from './pulseHelpers.js'

const POD_KEY_TO_NAME = { BP: 'Buyer', SP: 'Seller', FP: 'Foundations' }
const INITIAL_TIME_RANGE = 'all'
const CHAMPIONS_TIME_RANGE = '12m'

// Impact-tab sub-views — the QA manager cockpit (qa-live QA-2286).
const IMPACT_VIEWS = [
    { label: 'Overview', value: 'overview', emoji: '📊' },
    { label: 'Engineers', value: 'engineers', emoji: '🧭' },
    { label: 'Trends', value: 'trends', emoji: '📈' },
]

function mapAppPodToPulsePod(appPod) {
    if (appPod == null) return 'all'
    return POD_KEY_TO_NAME[appPod] ?? 'all'
}

export default function PulseTab({ selectedPod: appSelectedPod, refreshSignal = 0 }) {
    const [data, setData] = useState(null)
    const [qualityData, setQualityData] = useState(null)
    const [automationAiCount, setAutomationAiCount] = useState(0)
    const [automationNonAiCount, setAutomationNonAiCount] = useState(0)
    const [automationTotalTests, setAutomationTotalTests] = useState(0)
    const [automationAiUsagePct, setAutomationAiUsagePct] = useState(0)
    const [automationAiAuthors, setAutomationAiAuthors] = useState([])
    const [coverageCornerData, setCoverageCornerData] = useState(null)
    const [championsData, setChampionsData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [qualityLoading, setQualityLoading] = useState(true)
    const [automationAiLoading, setAutomationAiLoading] = useState(false)
    const [coverageCornerLoading, setCoverageCornerLoading] = useState(false)
    const [championsLoading, setChampionsLoading] = useState(false)
    const [error, setError] = useState(null)
    const [qualityError, setQualityError] = useState(null)
    const [coverageCornerError, setCoverageCornerError] = useState(null)
    const [championsError, setChampionsError] = useState(null)
    const [activeTab, setActiveTab] = useState('quality')
    const [impactView, setImpactView] = useState('overview')

    const selectedPod = mapAppPodToPulsePod(appSelectedPod)

    const fetchData = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await fetch('/api/pulse/velocity')
            if (!res.ok) throw new Error(`API returned ${res.status}`)
            setData(await res.json())
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load sprint data')
        } finally {
            setLoading(false)
        }
    }, [])

    const fetchQualityData = useCallback(async (range) => {
        setQualityLoading(true)
        setQualityError(null)
        try {
            const res = await fetch(`/api/pulse/quality?timeRange=${range}`)
            if (!res.ok) {
                const body = await res.json().catch(() => ({}))
                throw new Error(body?.details ?? body?.error ?? `Quality API returned ${res.status}`)
            }
            setQualityData(await res.json())
        } catch (e) {
            setQualityError(e instanceof Error ? e.message : 'Failed to load quality data')
        } finally {
            setQualityLoading(false)
        }
    }, [])

    const fetchChampionsData = useCallback(async (forceRefresh = false) => {
        setChampionsLoading(true)
        setChampionsError(null)
        try {
            const refreshQuery = forceRefresh ? '&refresh=1' : ''
            const res = await fetch(`/api/pulse/champions?timeRange=${CHAMPIONS_TIME_RANGE}${refreshQuery}`)
            if (!res.ok) {
                const body = await res.json().catch(() => ({}))
                throw new Error(body?.details ?? body?.error ?? `Champions API returned ${res.status}`)
            }
            setChampionsData(await res.json())
        } catch (e) {
            setChampionsError(e instanceof Error ? e.message : 'Failed to load champions')
        } finally {
            setChampionsLoading(false)
        }
    }, [])

    const fetchAutomationAiData = useCallback(async () => {
        setAutomationAiLoading(true)
        try {
            const res = await fetch('/api/pulse/automation-ai')
            if (!res.ok) {
                const body = await res.json().catch(() => ({}))
                throw new Error(body?.details ?? body?.error ?? `Automation AI API returned ${res.status}`)
            }
            const json = await res.json()
            setAutomationAiCount(json.aiCompletedTests ?? 0)
            setAutomationNonAiCount(json.nonAiCompletedTests ?? 0)
            setAutomationTotalTests(json.totalTests ?? 0)
            setAutomationAiUsagePct(json.aiUsagePct ?? 0)
            setAutomationAiAuthors(json.authors ?? [])
        } catch (e) {
            console.warn('Failed to load AICompleted test count:', e instanceof Error ? e.message : e)
            setAutomationAiCount(0)
            setAutomationNonAiCount(0)
            setAutomationTotalTests(0)
            setAutomationAiUsagePct(0)
            setAutomationAiAuthors([])
        } finally {
            setAutomationAiLoading(false)
        }
    }, [])

    const fetchCoverageCornerData = useCallback(async () => {
        setCoverageCornerLoading(true)
        setCoverageCornerError(null)
        try {
            const res = await fetch('/api/pulse/coverage-corner')
            if (!res.ok) {
                const body = await res.json().catch(() => ({}))
                throw new Error(body?.details ?? body?.error ?? `Coverage corner API returned ${res.status}`)
            }
            const json = await res.json()
            setCoverageCornerData(json)
            if (!json.success)
                setCoverageCornerError(json.details ?? json.error ?? 'Failed to load coverage corner data')
        } catch (e) {
            setCoverageCornerData(null)
            setCoverageCornerError(e instanceof Error ? e.message : 'Failed to load coverage corner data')
        } finally {
            setCoverageCornerLoading(false)
        }
    }, [])

    const refreshPulseData = useCallback(() => {
        fetchData()
        fetchQualityData(INITIAL_TIME_RANGE)
        fetchAutomationAiData()
        fetchCoverageCornerData()
        fetchChampionsData(true)
    }, [fetchAutomationAiData, fetchChampionsData, fetchCoverageCornerData, fetchData, fetchQualityData])

    useEffect(() => {
        fetchData()
        fetchQualityData(INITIAL_TIME_RANGE)
        fetchAutomationAiData()
        fetchCoverageCornerData()
        fetchChampionsData()
    }, [fetchAutomationAiData, fetchChampionsData, fetchCoverageCornerData, fetchData, fetchQualityData])

    useEffect(() => {
        if (!refreshSignal) return
        refreshPulseData()
    }, [refreshPulseData, refreshSignal])

    const aggregatedSprints = useMemo(() => (data ? aggregateSprints(data.pods, selectedPod) : []), [data, selectedPod])
    const currentSprint = useMemo(() => {
        if (!aggregatedSprints.length) return null
        return aggregatedSprints.find((s) => s.state === 'active') ?? aggregatedSprints[aggregatedSprints.length - 1]
    }, [aggregatedSprints])

    return (
        <div style={{ padding: '16px' }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: '16px',
                    gap: '12px',
                    flexWrap: 'wrap',
                }}
            >
                <div>
                    <h2 style={{ color: PULSE_COLORS.text, fontWeight: 700, fontSize: '24px', margin: 0 }}>QA Pulse</h2>
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', marginTop: '4px' }}>
                        Quality health &amp; event tracking across pods
                    </p>
                </div>
            </div>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                    gap: '12px',
                    flexWrap: 'wrap',
                }}
            >
                <div
                    style={{
                        display: 'inline-flex',
                        background: PULSE_COLORS.bgInner,
                        borderRadius: '8px',
                        padding: '4px',
                        border: `1px solid ${PULSE_COLORS.border}`,
                    }}
                >
                    <TabButton active={activeTab === 'quality'} onClick={() => setActiveTab('quality')}>
                        Quality
                    </TabButton>
                    <TabButton active={activeTab === 'automation'} onClick={() => setActiveTab('automation')}>
                        Automation
                    </TabButton>
                    <TabButton active={activeTab === 'impact'} onClick={() => setActiveTab('impact')}>
                        Impact
                    </TabButton>
                </div>
            </div>

            {activeTab === 'quality' && (
                <>
                    <SectionTitle>Current Sprint</SectionTitle>
                    <div style={{ marginBottom: '16px' }}>
                        <ExportableSection filename="pulse-current-sprint-score">
                            {({ captureRef, exportButton }) => (
                                <div ref={captureRef} style={{ height: '100%' }}>
                                    <SprintScoreGauge
                                        sprint={currentSprint}
                                        loading={loading && !data}
                                        headerAction={exportButton}
                                    />
                                </div>
                            )}
                        </ExportableSection>
                    </div>

                    <SectionTitle>Sprint Quality</SectionTitle>
                    {error && <ErrorBox>Failed to load sprint data: {error}</ErrorBox>}
                    {loading && !data && <LoadingGrid count={2} height={190} />}
                    {aggregatedSprints.length > 0 && (
                        <div style={{ marginBottom: '24px' }}>
                            <ExportableSection filename="pulse-qa-diagnostics">
                                <QaDiagnosticsChart sprints={aggregatedSprints} />
                            </ExportableSection>
                        </div>
                    )}

                    <SectionTitle>Quality Health</SectionTitle>

                    {qualityLoading && !qualityData && <LoadingGrid count={4} height={160} />}
                    {qualityError && <ErrorBox>Failed to load quality data: {qualityError}</ErrorBox>}

                    {qualityData && (
                        <>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
                                    gap: '16px',
                                    marginBottom: '16px',
                                    alignItems: 'stretch',
                                }}
                            >
                                <ExportableSection filename="pulse-defect-metrics">
                                    {({ captureRef, exportButton }) => (
                                        <div ref={captureRef} style={{ height: '100%' }}>
                                            <DefectMetrics
                                                total={qualityData.defects.total}
                                                byPriority={qualityData.defects.byPriority}
                                                byPod={qualityData.defects.byPod}
                                                selectedPod={selectedPod}
                                                headerAction={exportButton}
                                            />
                                        </div>
                                    )}
                                </ExportableSection>
                                <ExportableSection filename="pulse-qa-failed-metrics">
                                    {({ captureRef, exportButton }) => (
                                        <div ref={captureRef} style={{ height: '100%' }}>
                                            <QaFailedMetrics
                                                qaFailed={qualityData.defects.qaFailed}
                                                selectedPod={selectedPod}
                                                headerAction={exportButton}
                                            />
                                        </div>
                                    )}
                                </ExportableSection>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <ExportableSection filename="pulse-work-status">
                                    {({ captureRef, exportButton }) => (
                                        <div ref={captureRef}>
                                            <QaStoryStatus
                                                passedQa={qualityData.qaStories.passedQa}
                                                inQa={qualityData.qaStories.inQa}
                                                needsVerification={qualityData.qaStories.needsVerification}
                                                inProgress={qualityData.qaStories.inProgress}
                                                byPod={qualityData.qaStories.byPod}
                                                aiUsage={qualityData.qaStories.aiUsage}
                                                selectedPod={selectedPod}
                                                headerAction={exportButton}
                                            />
                                        </div>
                                    )}
                                </ExportableSection>
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <ExportableSection filename="pulse-release-comparison">
                                    {({ captureRef, exportButton }) => (
                                        <div ref={captureRef}>
                                            <ReleaseComparison
                                                hotfix={qualityData.releases.hotfix}
                                                patch={qualityData.releases.patch}
                                                release={qualityData.releases.release}
                                                headerAction={exportButton}
                                            />
                                        </div>
                                    )}
                                </ExportableSection>
                            </div>
                        </>
                    )}
                </>
            )}

            {activeTab === 'automation' && (
                <>
                    <SectionTitle>Automation Coverage & Adoption</SectionTitle>

                    {qualityLoading && !qualityData && <LoadingGrid count={2} height={260} />}
                    {qualityError && <ErrorBox>Failed to load automation data: {qualityError}</ErrorBox>}
                    {coverageCornerError && (
                        <WarningBox>Coverage snapshot is temporarily unavailable: {coverageCornerError}</WarningBox>
                    )}

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
                            gap: '16px',
                            marginBottom: '24px',
                            alignItems: 'start',
                        }}
                    >
                        <ExportableSection filename="pulse-automation-coverage">
                            {({ captureRef, exportButton }) => (
                                <div ref={captureRef}>
                                    <AutomationCoverageInsights
                                        data={coverageCornerData}
                                        loading={coverageCornerLoading}
                                        headerAction={exportButton}
                                        automationSummary={
                                            qualityData ? (
                                                <AutomationCorner
                                                    data={qualityData.automation}
                                                    selectedPod={selectedPod}
                                                    embedded
                                                />
                                            ) : null
                                        }
                                    />
                                </div>
                            )}
                        </ExportableSection>

                        <div style={{ display: 'grid', gap: '16px' }}>
                            <ExportableSection filename="pulse-ai-adoption">
                                {({ captureRef, exportButton }) => (
                                    <div ref={captureRef}>
                                        <AiAutomationInsights
                                            aiCompletedTests={automationAiCount}
                                            nonAiCompletedTests={automationNonAiCount}
                                            totalTests={automationTotalTests}
                                            aiUsagePct={automationAiUsagePct}
                                            authors={automationAiAuthors}
                                            loading={automationAiLoading}
                                            headerAction={exportButton}
                                        />
                                    </div>
                                )}
                            </ExportableSection>
                            <CoverageWinsBulletin
                                wins={coverageCornerData?.recentWins ?? []}
                                loading={coverageCornerLoading}
                            />
                        </div>
                    </div>

                    <SectionTitle>AI Quality Analysis</SectionTitle>
                    <div style={{ marginBottom: '24px' }}>
                        <AIQualityAnalysis />
                    </div>
                </>
            )}

            {activeTab === 'impact' && (
                <>
                    <div style={{ marginBottom: '8px' }}>
                        <h2 style={{ color: PULSE_COLORS.text, fontWeight: 700, fontSize: '20px', margin: 0 }}>
                            🏆 QA Impact
                        </h2>
                        <p style={{ marginTop: '2px', fontSize: '13px', color: PULSE_COLORS.textMuted }}>
                            Who&apos;s doing what across the quality team — detection, automation, resolution, and where
                            each engineer is excelling or can grow.
                        </p>
                    </div>

                    {/* Impact sub-tabs */}
                    <div
                        style={{
                            display: 'flex',
                            gap: '4px',
                            marginBottom: '24px',
                            borderBottom: `1px solid ${PULSE_COLORS.border}`,
                        }}
                    >
                        {IMPACT_VIEWS.map((v) => (
                            <button
                                key={v.value}
                                onClick={() => setImpactView(v.value)}
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    background: 'transparent',
                                    border: 'none',
                                    borderBottom: `2px solid ${impactView === v.value ? PULSE_COLORS.primary : 'transparent'}`,
                                    marginBottom: '-1px',
                                    cursor: 'pointer',
                                    color: impactView === v.value ? PULSE_COLORS.primary : PULSE_COLORS.textMuted,
                                }}
                            >
                                {v.emoji} {v.label}
                            </button>
                        ))}
                    </div>

                    {championsError && <ErrorBox>Failed to load champions: {championsError}</ErrorBox>}
                    {championsLoading && !championsData && <LoadingGrid count={2} height={260} />}

                    {/* ── Overview ─────────────────────────────────── */}
                    {impactView === 'overview' && (
                        <>
                            {championsData && (
                                <div style={{ marginBottom: '24px' }}>
                                    <ExportableSection filename="pulse-qa-impact-summary">
                                        {({ captureRef, exportButton }) => (
                                            <div ref={captureRef}>
                                                <QaImpactSummary
                                                    members={championsData.members}
                                                    headerAction={exportButton}
                                                />
                                            </div>
                                        )}
                                    </ExportableSection>
                                </div>
                            )}

                            {aggregatedSprints.length > 0 && (
                                <div style={{ marginBottom: '24px' }}>
                                    <ExportableSection filename="pulse-qa-momentum">
                                        {({ captureRef, exportButton }) => (
                                            <div ref={captureRef}>
                                                <QaMomentum sprints={aggregatedSprints} headerAction={exportButton} />
                                            </div>
                                        )}
                                    </ExportableSection>
                                </div>
                            )}

                            {championsData && (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
                                        gap: '16px',
                                        marginBottom: '24px',
                                        alignItems: 'stretch',
                                    }}
                                >
                                    <ExportableSection filename="pulse-qa-leaderboard">
                                        {({ captureRef, exportButton }) => (
                                            <div ref={captureRef} style={{ height: '100%' }}>
                                                <Leaderboard
                                                    members={championsData.members}
                                                    headerAction={exportButton}
                                                />
                                            </div>
                                        )}
                                    </ExportableSection>
                                    <ExportableSection filename="pulse-badge-champions">
                                        {({ captureRef, exportButton }) => (
                                            <div ref={captureRef} style={{ height: '100%' }}>
                                                <BadgeChampions
                                                    members={championsData.members}
                                                    headerAction={exportButton}
                                                />
                                            </div>
                                        )}
                                    </ExportableSection>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Engineers ────────────────────────────────── */}
                    {impactView === 'engineers' && championsData && (
                        <>
                            <div style={{ marginBottom: '24px' }}>
                                <ExportableSection filename="pulse-qa-team-comparison">
                                    {({ captureRef, exportButton }) => (
                                        <div ref={captureRef}>
                                            <QaTeamComparison
                                                members={championsData.members}
                                                headerAction={exportButton}
                                            />
                                        </div>
                                    )}
                                </ExportableSection>
                            </div>
                            <div style={{ marginBottom: '24px' }}>
                                <ExportableSection filename="pulse-qa-engineer-scorecards">
                                    {({ captureRef, exportButton }) => (
                                        <div ref={captureRef}>
                                            <QaEngineerScorecards
                                                members={championsData.members}
                                                headerAction={exportButton}
                                            />
                                        </div>
                                    )}
                                </ExportableSection>
                            </div>
                        </>
                    )}

                    {/* ── Trends ───────────────────────────────────── */}
                    {impactView === 'trends' && (
                        <>
                            {aggregatedSprints.length > 0 && (
                                <div style={{ marginBottom: '24px' }}>
                                    <ExportableSection filename="pulse-qa-sprint-history">
                                        {({ captureRef, exportButton }) => (
                                            <div ref={captureRef}>
                                                <QaSprintHistory
                                                    sprints={aggregatedSprints}
                                                    headerAction={exportButton}
                                                />
                                            </div>
                                        )}
                                    </ExportableSection>
                                </div>
                            )}
                            {aggregatedSprints.length > 0 && (
                                <div style={{ marginBottom: '24px' }}>
                                    <ExportableSection filename="pulse-quality-engineer-progress">
                                        <QualityEngineerProgressChart sprints={aggregatedSprints} />
                                    </ExportableSection>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    )
}

function TabButton({ active, onClick, children }) {
    return (
        <button
            onClick={onClick}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 700,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: active ? PULSE_COLORS.primary : 'transparent',
                color: active ? '#fff' : PULSE_COLORS.textMuted,
                transition: 'all 0.15s',
            }}
        >
            {children}
        </button>
    )
}

function SectionTitle({ children }) {
    return (
        <h2
            style={{
                color: PULSE_COLORS.text,
                fontWeight: 700,
                fontSize: '20px',
                marginTop: '8px',
                marginBottom: '12px',
            }}
        >
            {children}
        </h2>
    )
}

function ErrorBox({ children }) {
    return (
        <div
            style={{
                padding: '12px 16px',
                marginBottom: '16px',
                background: '#7f1d1d33',
                border: '1px solid #b91c1c',
                borderRadius: '8px',
                color: '#FECDD3',
                fontSize: '13px',
            }}
        >
            {children}
        </div>
    )
}

function WarningBox({ children }) {
    return (
        <div
            style={{
                padding: '12px 16px',
                marginBottom: '16px',
                background: '#78350f33',
                border: '1px solid #92400e',
                borderRadius: '8px',
                color: '#FDE68A',
                fontSize: '13px',
            }}
        >
            {children}
        </div>
    )
}

function LoadingGrid({ count, height }) {
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '16px',
                marginBottom: '24px',
            }}
        >
            {[...Array(count)].map((_, i) => (
                <div key={i} style={{ height: `${height}px`, borderRadius: '12px', background: PULSE_COLORS.border }} />
            ))}
        </div>
    )
}
